// Page side of Web Push (plan §8). Permission is requested only from a user gesture on the Me page.
// Policy for shared devices: a subscription made by another account is never silently re-bound to the
// signed-in user. We re-bind on sign-in only when the stored identity already belongs to THIS user.
import { ApiError, get, post } from './api';
import { clearPushIdentity, getPushIdentity, setPushIdentity } from './pushIdentity';

export type PushState = 'unsupported' | 'needs-install' | 'denied' | 'unavailable' | 'off' | 'on';

const isIOS = () => typeof navigator !== 'undefined' && (/iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1));
const isStandalone = () => typeof window !== 'undefined' && (window.matchMedia?.('(display-mode: standalone)').matches || (navigator as any).standalone === true);

export function pushSupported(): boolean {
  return typeof window !== 'undefined' && 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
}

function keyBytes(b64url: string): Uint8Array<ArrayBuffer> {
  const pad = '='.repeat((4 - (b64url.length % 4)) % 4);
  const raw = atob((b64url + pad).replace(/-/g, '+').replace(/_/g, '/'));
  const out = new Uint8Array(new ArrayBuffer(raw.length));
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

async function registration(): Promise<ServiceWorkerRegistration | null> {
  if (!('serviceWorker' in navigator)) return null;
  try {
    return await Promise.race([navigator.serviceWorker.ready, new Promise<null>((r) => setTimeout(() => r(null), 4000))]);
  } catch {
    return null;
  }
}

export async function getPushState(meId: string): Promise<PushState> {
  if (!pushSupported()) return isIOS() && !isStandalone() ? 'needs-install' : 'unsupported';
  if (isIOS() && !isStandalone()) return 'needs-install';
  if (Notification.permission === 'denied') return 'denied';
  try {
    const cfg = await get<{ enabled: boolean }>('/api/push/config');
    if (!cfg.enabled) return 'unavailable';
  } catch {
    return 'unavailable';
  }
  if (Notification.permission !== 'granted') return 'off';
  const reg = await registration();
  const sub = await reg?.pushManager.getSubscription();
  if (!sub) return 'off';
  const id = await getPushIdentity();
  if (!id || id.uid !== meId || id.endpoint !== sub.endpoint) return 'off';
  try {
    const c = await post<{ bound: boolean }>('/api/push/check', { endpoint: sub.endpoint });
    return c.bound ? 'on' : 'off';
  } catch {
    return 'on'; // offline: trust the local record
  }
}

/** Must run inside a click handler: the permission prompt needs a user gesture (iOS especially). */
export async function enablePush(meId: string, deviceLabel?: string): Promise<'on' | 'denied'> {
  const perm = Notification.permission === 'granted' ? 'granted' : await Notification.requestPermission();
  if (perm === 'denied') return 'denied';
  if (perm !== 'granted') throw new Error('Notification permission was not granted.');
  const reg = await registration();
  if (!reg) throw new Error('Service worker is not ready yet. Try again in a moment.');
  const cfg = await get<{ enabled: boolean; publicKey: string | null }>('/api/push/config');
  if (!cfg.enabled || !cfg.publicKey) throw new Error('Push notifications are turned off for this workspace.');
  let sub = await reg.pushManager.getSubscription();
  if (sub) {
    // A subscription made with a different server key (e.g. VAPID rotated) can't be reused.
    const cur = sub.options?.applicationServerKey;
    if (cur && new Uint8Array(cur).join() !== keyBytes(cfg.publicKey).join()) {
      await sub.unsubscribe();
      sub = null;
    }
  }
  sub ??= await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: keyBytes(cfg.publicKey) });
  const json = sub.toJSON();
  try {
    const bound = await post<{ uid: string; bk: string }>('/api/push/subscribe', { endpoint: sub.endpoint, keys: json.keys, deviceLabel: deviceLabel ?? null });
    await setPushIdentity({ uid: bound.uid, bk: bound.bk, endpoint: sub.endpoint });
  } catch (e) {
    await sub.unsubscribe().catch(() => undefined);
    throw e;
  }
  return 'on';
}

export async function disablePush(): Promise<void> {
  const reg = await registration();
  const sub = await reg?.pushManager.getSubscription();
  if (sub) {
    await post('/api/push/unsubscribe', { endpoint: sub.endpoint }).catch((e) => {
      if (!(e instanceof ApiError) || e.status >= 500) throw e;
    });
    await sub.unsubscribe().catch(() => undefined);
  }
  await clearPushIdentity();
}

/** Called after sign-in: keep live bindings, clear revoked/rotated ones; enabling again is a user gesture. */
export async function rebindIfOwned(meId: string): Promise<void> {
  if (!pushSupported() || Notification.permission !== 'granted') return;
  const id = await getPushIdentity();
  if (!id) return;
  if (id.uid !== meId) {
    await clearPushIdentity(); // someone else's: the worker falls back to generic notices
    return;
  }
  const reg = await registration();
  const sub = await reg?.pushManager.getSubscription();
  if (!sub || sub.endpoint !== id.endpoint) return;
  try {
    const c = await post<{ bound: boolean }>('/api/push/check', { endpoint: sub.endpoint });
    if (c.bound) return;
    // A deleted binding (logout/revocation/key rotation) requires fresh opt-in, never silently rebind.
    await sub.unsubscribe().catch(() => undefined);
    await clearPushIdentity();
    return;
  } catch {
    /* offline or push disabled: try again next sign-in */
  }
}
